// Deliberately a copy of ploy-client's src/state/accessPaths.ts: the two apps
// share no bundle, and the names are kept identical so the contract reads the
// same in both repos. It lives in state/ because state/members/accessReviews.ts
// types its rows with it.

import { format, parseISO } from "date-fns";

export type MFAccessKind = "direct" | "indirect";

export type MFAccessPathEntitlement = {
  id: string;
  name: string;
  type?: string | null;
};

export type MFAccessPathProvenance = {
  dataSource?: string | null;
  dataSourceDetail?: string | null;
  dataSourceLabel?: string | null;
  dataAsOf?: string | null;
};

export type MFAccessPathHop = {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  accessId: string;
  entitlements: MFAccessPathEntitlement[];
  provenance?: MFAccessPathProvenance | null;
  parentResourceId?: string | null;
  parentResourceName?: string | null;
  integrationId?: string | null;
};

export type MFAccessPathHopOwnership = {
  parentResourceId: string | null;
  parentResourceName: string | null;
  integrationId: string | null;
};

export type MFAccessPath = {
  leafAccessId: string;
  grantedAt?: string | null;
  expiresAt?: string | null;
  hops: MFAccessPathHop[];
};

export const ELIGIBLE_ENTITLEMENT_TOOLTIP =
  "Eligible via PIM, not an active assignment";

export const isEligibleEntitlement = (
  entitlement: Pick<MFAccessPathEntitlement, "name">,
): boolean => entitlement.name.trim().toLowerCase() === "eligible";

const presentString = (value?: string | null): string | null => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed === "" ? null : trimmed;
};

export const hopOwnership = (
  hop: Partial<
    Pick<
      MFAccessPathHop,
      "parentResourceId" | "parentResourceName" | "integrationId"
    >
  >,
): MFAccessPathHopOwnership => ({
  parentResourceId: presentString(hop.parentResourceId),
  parentResourceName: presentString(hop.parentResourceName),
  integrationId: presentString(hop.integrationId),
});

export const hopLabel = (
  hop: Pick<MFAccessPathHop, "resourceName"> &
    Partial<Pick<MFAccessPathHop, "parentResourceName">>,
): string => {
  const parent = hopOwnership(hop).parentResourceName;
  return parent ? `${hop.resourceName} (${parent})` : hop.resourceName;
};

export const accessKindOf = (account: {
  accessKind?: MFAccessKind | null;
}): MFAccessKind => (account.accessKind === "indirect" ? "indirect" : "direct");

export const isIndirectAccount = (account: {
  accessKind?: MFAccessKind | null;
}): boolean => accessKindOf(account) === "indirect";

export const accessKindLabel = (kind?: MFAccessKind | null): string =>
  kind === "indirect" ? "Indirect" : "Direct";

const sentence = (value: string): string => {
  const words = value.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const provenanceStamp = (
  provenance?: MFAccessPathProvenance | null,
): string | null => {
  if (!provenance) return null;

  const origin =
    provenance.dataSourceLabel ??
    (provenance.dataSource ? sentence(provenance.dataSource) : null);
  if (!origin) return null;

  if (!provenance.dataAsOf) return origin;
  try {
    return `${origin} · ${format(parseISO(provenance.dataAsOf), "MMM d yyyy")}`;
  } catch {
    return origin;
  }
};
